export interface AlphaplanChannel {
    id: string;
    name: string;
}

export interface AlphaplanIntegration {
    id: string;
    createdAt: string;
    updatedAt: string;
    name: string;
    baseUrl: string;
    hasApiKey: boolean;
    hasPassword: boolean;
    hasCaBundle: boolean;
    username: string;
    proxy: string;
    supplierFilter: string;
    supplierNumberField: string;
    purchaseOrderBelegType: number;
    openPoFilter: string;
    ocAbNumberField: string;
    // choice fields arrive as their stored lower-case values (convert_choices_to_enum = False)
    ocWriteMode: string;
    ocWriteOnConvertedPo: boolean;
    ocPositionMemoField: string;
    ocHeaderMemoField: string;
    ocToleranceBlock: boolean;
    readMode: string;
    attachSourceDocuments: boolean;
    // capabilities and integrationParameters are graphene JSONString scalars: JSON encoded as a string
    capabilities: string | null;
    integrationParameters: string | null;
    orderConfirmationChannels: AlphaplanChannel[];
    invoiceChannels: AlphaplanChannel[];
    masterDataUpdateActive: boolean;
    masterDataUpdatedAt: string | null;
    masterDataUpdateError: string | null;
    fetchProgress: number;
}

export interface AlphaplanParameterDefinition {
    key: string;
    type: string;
    // also a JSONString: the default already arrives JSON encoded
    default: string | null;
    description: string;
}

export interface AlphaplanConnectionTestResult {
    ok: boolean;
    warnings: string[];
    errors: string[];
    // JSONString: one boolean per probe plus `writes: "unverified"`
    capabilities: string | null;
    // JSONString: probe name -> ok / warning / error
    probes: string | null;
    version: string | null;
    licence: number | null;
    fullname: string | null;
    batchMax: number | null;
}

export interface AlphaplanFormData {
    name: string;
    baseUrl: string;
    apiKey: string;
    username: string;
    password: string;
    orderConfirmationChannelIds: string[];
    invoiceChannelIds: string[];
    attachSourceDocuments: boolean;
    proxy: string;
    caBundle: string;
    supplierFilter: string;
    supplierNumberField: string;
    purchaseOrderBelegType: string;
    openPoFilter: string;
    ocAbNumberField: string;
    ocWriteMode: string;
    ocWriteOnConvertedPo: boolean;
    ocPositionMemoField: string;
    ocHeaderMemoField: string;
    ocToleranceBlock: boolean;
    readMode: string;
    integrationParameters: Record<string, any>;
}

export const OC_WRITE_MODE_DATES_AND_NOTES = 'dates_and_notes';
export const OC_WRITE_MODE_OVERWRITE_PRICES_QUANTITIES = 'overwrite_prices_quantities';

export const READ_MODE_SQL = 'sql';
export const READ_MODE_REST = 'rest';

/** Partial master-data run that only refreshes the open purchase orders. */
export const ORDERS_RECORD_TYPE = 'orders';

/** Capability maps carry these bookkeeping keys next to the boolean probe flags. */
export const CAPABILITY_VERSION_KEY = 'version';
export const CAPABILITY_CHECKED_AT_KEY = 'checked_at';
export const CAPABILITY_LAST_ERROR_KEY = 'last_error';
export const CAPABILITY_WRITES_KEY = 'writes';

export const PROBE_STATUS_OK = 'ok';
export const PROBE_STATUS_WARNING = 'warning';
export const PROBE_STATUS_ERROR = 'error';

// the backend model defaults (integrations/alphaplan/settings.py)
export const FORM_DEFAULT_VALUES: AlphaplanFormData = {
    name: '',
    baseUrl: '',
    apiKey: '',
    username: '',
    password: '',
    orderConfirmationChannelIds: [],
    invoiceChannelIds: [],
    attachSourceDocuments: true,
    proxy: '',
    caBundle: '',
    supplierFilter: 'IstLieferant=1',
    supplierNumberField: 'AdressNummer',
    purchaseOrderBelegType: '9',
    openPoFilter: 'IstArchiviert=0|IstStorniert=0',
    ocAbNumberField: 'IhrAuftrag',
    ocWriteMode: OC_WRITE_MODE_DATES_AND_NOTES,
    ocWriteOnConvertedPo: true,
    ocPositionMemoField: 'ZusatzText1',
    ocHeaderMemoField: '',
    ocToleranceBlock: false,
    readMode: READ_MODE_SQL,
    integrationParameters: {},
};

/** Parse an integer field value; empty or non-integer input means "not set" and is sent as null. */
export const parseIntegerField = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).trim());
    return Number.isInteger(parsed) ? parsed : null;
};

/** Decode a graphene JSONString into an object; anything unparsable becomes an empty object. */
export const parseJsonStringObject = (value: string | null | undefined): Record<string, any> => {
    if (!value) return {};
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

/** Map an integration loaded from the API onto the form state. Secrets are never prefilled. */
export const integrationToFormData = (integration: AlphaplanIntegration): AlphaplanFormData => ({
    ...FORM_DEFAULT_VALUES,
    name: integration.name,
    baseUrl: integration.baseUrl,
    apiKey: '',
    username: integration.username ?? '',
    password: '',
    orderConfirmationChannelIds: integration.orderConfirmationChannels.map((channel) => channel.id),
    invoiceChannelIds: integration.invoiceChannels.map((channel) => channel.id),
    attachSourceDocuments: integration.attachSourceDocuments,
    proxy: integration.proxy ?? '',
    caBundle: '',
    supplierFilter: integration.supplierFilter ?? '',
    supplierNumberField: integration.supplierNumberField ?? '',
    purchaseOrderBelegType:
        integration.purchaseOrderBelegType === null || integration.purchaseOrderBelegType === undefined
            ? ''
            : String(integration.purchaseOrderBelegType),
    openPoFilter: integration.openPoFilter ?? '',
    ocAbNumberField: integration.ocAbNumberField ?? '',
    ocWriteMode: (integration.ocWriteMode || OC_WRITE_MODE_DATES_AND_NOTES).toLowerCase(),
    ocWriteOnConvertedPo: integration.ocWriteOnConvertedPo,
    ocPositionMemoField: integration.ocPositionMemoField ?? '',
    ocHeaderMemoField: integration.ocHeaderMemoField ?? '',
    ocToleranceBlock: integration.ocToleranceBlock,
    readMode: (integration.readMode || READ_MODE_SQL).toLowerCase(),
    integrationParameters: parseJsonStringObject(integration.integrationParameters),
});
